import React from 'react';
import {
  Box, HStack, Text, Button, Icon, Tooltip, TooltipContent, TooltipText,
} from '@gluestack-ui/themed';
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from 'lucide-react-native';
import { TablePaginationProps } from '../types';

/**
 * TablePagination - Footer controls for navigating through DataTable pages.
 *
 * It provides:
 * - Summary text of currently displayed row range (e.g., "Rows 1-20 of 100").
 * - Navigation buttons for First, Previous, Next, and Last pages.
 * - Tooltips for each navigation action.
 * - Automatic disabling of controls during loading or when at the boundaries.
 *
 * @param {TablePaginationProps} props - Configuration for total items, current page, and navigation callbacks.
 * @returns {React.ReactElement} The rendered pagination toolbar.
 */
const TablePagination: React.FC<TablePaginationProps> = ({
  total,
  page,
  pageSize,
  onPageChange,
  isLoading,
  isActionRunning,
  isSelectionMode = false,
  height,
  noPadding,
}) => {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = total ? 1 + (currentPage - 1) * pageSize : 0;
  const end = total ? Math.min(total, currentPage * pageSize) : 0;

  const leftDisabled = currentPage === 1 || isLoading || isActionRunning || !total || isSelectionMode;
  const rightDisabled = currentPage === totalPages || isLoading || isActionRunning || !total || isSelectionMode;

  return (
    <Box justifyContent="center" height={height} mt="$2" px={noPadding ? 0 : '$6'}>
      <HStack alignItems="center" justifyContent="space-between">
        <Text size="2xs" color="$textSubtle">
          {total ? `Rows ${start}-${end} of ${total}` : ' '}
          {isSelectionMode && ' (Pagination disabled during bulk selection)'}
        </Text>
        <HStack space="xs" alignItems="center">
          <Tooltip
            placement="top"
            trigger={(triggerProps) => (
              <Button {...triggerProps} variant="link" isDisabled={leftDisabled} onPress={() => onPageChange(1)}>
                <Icon as={ChevronsLeft} size="sm" color={leftDisabled ? '$textHint' : '$textBody'} />
              </Button>
            )}
          >
            <TooltipContent>
              <TooltipText>{isSelectionMode ? 'Disabled during bulk selection' : 'First Page'}</TooltipText>
            </TooltipContent>
          </Tooltip>

          <Tooltip
            placement="top"
            trigger={(triggerProps) => (
              <Button
                {...triggerProps}
                variant="link"
                isDisabled={leftDisabled}
                onPress={() => onPageChange(Math.max(1, currentPage - 1))}
              >
                <Icon as={ChevronLeft} size="sm" color={leftDisabled ? '$textHint' : '$textBody'} />
              </Button>
            )}
          >
            <TooltipContent>
              <TooltipText>{isSelectionMode ? 'Disabled during bulk selection' : 'Previous Page'}</TooltipText>
            </TooltipContent>
          </Tooltip>

          <Text size="xs" mx="$2">{`Page ${currentPage} of ${totalPages}`}</Text>

          <Tooltip
            placement="top"
            trigger={(triggerProps) => (
              <Button
                {...triggerProps}
                variant="link"
                isDisabled={rightDisabled}
                onPress={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              >
                <Icon as={ChevronRight} size="sm" color={rightDisabled ? '$textHint' : '$textBody'} />
              </Button>
            )}
          >
            <TooltipContent>
              <TooltipText>{isSelectionMode ? 'Disabled during bulk selection' : 'Next Page'}</TooltipText>
            </TooltipContent>
          </Tooltip>

          <Tooltip
            placement="top"
            trigger={(triggerProps) => (
              <Button
                {...triggerProps}
                variant="link"
                isDisabled={rightDisabled}
                onPress={() => onPageChange(totalPages)}
              >
                <Icon as={ChevronsRight} size="sm" color={rightDisabled ? '$textHint' : '$textBody'} />
              </Button>
            )}
          >
            <TooltipContent>
              <TooltipText>{isSelectionMode ? 'Disabled during bulk selection' : 'Last Page'}</TooltipText>
            </TooltipContent>
          </Tooltip>
        </HStack>
      </HStack>
    </Box>
  );
};

export default TablePagination;
